import { IDocumentFileModel, IDocumentFolderModel, IExternalLinkForm, IExternalLinkModel } from '@api/models';
import { getResellerIdFromRedux } from '@utils/common';
import http from 'httpConfig/http';

/* =================================================================================================
 * Document Folder Endpoints
 * ===============================================================================================*/

export const getAllDocumentFolders = async (clientId: string) => {
    const { data } = await http.get(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders`
    );

    return data as IDocumentFolderModel[];
};

export const createDocumentFolder = async (clientId: string, payload: Pick<IDocumentFolderModel, 'name'>) => {
    const { data } = await http.post(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders`,
        payload
    );

    return data as IDocumentFolderModel;
};

export const updateDocumentFolder = async (
    clientId: string,
    folderId: string,
    payload: Pick<IDocumentFolderModel, 'name'>
) => {
    const { data } = await http.put(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders/${folderId}`,
        payload
    );

    return data as IDocumentFolderModel;
};

export const deleteDocumentFolder = async (clientId: string, folderId: string) => {
    await http.delete(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders/${folderId}`
    );
};

export const moveDocumentFolder = async (clientId: string, folderId: string, payload: { parentId: string | null }) => {
    const { data } = await http.patch(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders/${folderId}/move`,
        payload
    );

    return data as IDocumentFolderModel;
};

/* =================================================================================================
 * Document File Endpoints
 * ===============================================================================================*/

export const getAllDocuments = async (clientId: string) => {
    const { data } = await http.get(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/files`
    );

    return data as IDocumentFileModel[];
};

export const uploadDocument = async (
    clientId: string,
    payload: FormData,
    config: { signal?: AbortSignal; onUploadProgress?: (progress: number) => void }
) => {
    const { data } = await http.post(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/files`,
        payload,
        {
            signal: config.signal,
            onUploadProgress: (progressEvent) => {
                const progress = (progressEvent.loaded / progressEvent.total) * 100;

                config?.onUploadProgress?.(Math.trunc(progress));
            },
        }
    );

    return data as IDocumentFileModel;
};

// export const addUserToDocument = async (clientId: string, fileId: string, userIds: string[]) => {
//     await http.post(
//         `${
//             import.meta.env.VITE_APP_API_URL
//         }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}/users`,
//         {
//             users: userIds,
//         }
//     );
// };
export const addUserToDocument = async (clientId: string, fileId: string, userIds: string[], folderId?: string) => {
    await http.post(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}/users`,
        {
            users: userIds,
            folderId: folderId,
        }
    );
};

export const removeUserFromDocument = async (clientId: string, fileId: string, userId: string) => {
    await http.delete(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}/users/${userId}`
    );
};

export const moveFileToFolder = async (clientId: string, fileId: string, payload: { folder: string | null }) => {
    const { data } = await http.patch(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}/move`,
        payload
    );

    return data as IDocumentFileModel;
};

export const renameDocumentFile = async (
    clientId: string,
    fileId: string,
    payload: Pick<IDocumentFileModel, 'name'>
) => {
    const { data } = await http.put(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}`,
        payload
    );

    return data as IDocumentFileModel;
};

/* =================================================================================================
 * Folder Access Endpoints
 * ===============================================================================================*/

export const addUserToFolder = async (clientId: string, folderId: string, userIds: string[]) => {
    await http.post(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/folders/${folderId}/users`,
        {
            users: userIds,
        }
    );
};
export const removeUserFromFolder = async (clientId: string, folderId: string, userId: string) => {
    await http.delete(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/folder/${folderId}/users/${userId}`
    );
};

export const deleteDocument = async (clientId: string, fileId: string) => {
    await http.delete(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/${fileId}`
    );
};

/* =================================================================================================
 * External Link Endpoints
 * ===============================================================================================*/

export const getAllExternalLinks = async (clientId: string) => {
    const { data } = await http.get(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links`
    );

    return data as IExternalLinkModel[];
};

export const addExternalLink = async (clientId: string, payload: IExternalLinkForm) => {
    const { data } = await http.post(
        `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links`,
        payload
    );

    return data as IExternalLinkModel;
};

export const deleteExternalLink = async (clientId: string, externalLinkId: string) => {
    await http.delete(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links/${externalLinkId}`
    );
};

// export const addUserToExternalLink = async (clientId: string, externalLinkId: string, userIds: string[]) => {
//     await http.post(
//         `${
//             import.meta.env.VITE_APP_API_URL
//         }/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links/${externalLinkId}/users`,
//         {
//             users: userIds,
//         }
//     );
// };
export const addUserToExternalLink = async (
    clientId: string,
    externalLinkId: string,
    userIds: string[],
    folderId: string
) => {
    await http.post(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links/${externalLinkId}/users`,
        {
            users: userIds,
            folderId: folderId,
        }
    );
};

export const removeUserFromExternalLink = async (clientId: string, externalLinkId: string, userId: string) => {
    await http.delete(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links/${externalLinkId}/users/${userId}`
    );
};

export const moveExternalLinkToFolder = async (
    clientId: string,
    externalLinkId: string,
    payload: { folder: string | null }
) => {
    await http.patch(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/external-links/${externalLinkId}/move`,
        payload
    );

    // return data as IExternalLinkModel;
};

export const renameExternalLinkDocument = async (
    clientId: string,
    externalLinkId: string,
    payload: Pick<IExternalLinkForm, 'name'>
) => {
    const { data } = await http.put(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents//external-links/${externalLinkId}`,
        payload
    );

    return data as IExternalLinkModel;
};

/* =================================================================================================
 * Document Multipart File Endpoints
 * ===============================================================================================*/

interface InitMultipartResponse {
    uploadId: string;
    key: string;
    bucket: string;
}

interface GetPartUrlResponse {
    url: string;
}

export interface UploadPart {
    PartNumber: number;
    ETag: string;
}
export type BulkInitMultipartUploadPayload = {
    files: {
        name: string;
        fileSizeInBytes: number;
        contentType: string;
        extension: string;
    }[];
};

export type BulkInitMultipartUploadItem = {
    name: string;
    success: boolean;
    reason: string | null;
    uploadId: string;
    key: string;
    bucket: string;
};

export const initMultipartUpload = async (
    clientId: string,
    payload: {
        name: string;
        contentType: string;
        fileSizeInBytes: number;
        users: string[];
        folderId?: string;
        extension: string;
    }
) => {
    const { data } = await http.post<InitMultipartResponse>(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/multipart/init`,
        payload
    );
    return data;
};

export const initMultipartUploadBulk = async (clientId: string, payload: BulkInitMultipartUploadPayload) => {
    const { data } = await http.post<BulkInitMultipartUploadItem[]>(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/multipart/bulk/init`,
        payload
    );

    return data;
};

export const getPartUploadUrl = async (
    clientId: string,
    payload: {
        key: string;
        uploadId: string;
        partNumber: number;
        contentType: string;
    }
) => {
    const { data } = await http.post<GetPartUrlResponse>(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/multipart/part-url`,
        payload
    );
    return data;
};

export const completeMultipartUpload = async (
    clientId: string,
    payload: {
        key: string;
        uploadId: string;
        parts: UploadPart[];
        name: string;
        folderId?: string;
        fileSizeInBytes: number;
        users: string[];
    }
) => {
    const { data } = await http.post(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/multipart/complete`,
        payload
    );
    return data as IDocumentFileModel;
};

export const abortMultipartUpload = async (clientId: string, payload: { key: string; uploadId: string }) => {
    await http.post(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/documents/files/multipart/abort`,
        payload
    );
};

export const getStorageDetails = async (clientId: string, userId: string) => {
    const { data } = await http.get(
        `${
            import.meta.env.VITE_APP_API_URL
        }/${getResellerIdFromRedux()}/clients/${clientId}/users/${userId}/storage-space`
    );
    console.log('datta in stoage details', data);

    return data;
};
